import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api, { authAPI } from '../services/api'

export const useUserStore = defineStore('user', () => {
    // State
    const user = ref(null)
    const token = ref(localStorage.getItem('token'))
    const isInitialized = ref(false)
    const loginMethod = ref(localStorage.getItem('loginMethod')) // Track which method was used for login

    // Getters
    const isAuthenticated = computed(() => {
        return !!token.value && !!user.value
    })

    const isAdmin = computed(() => {
        return user.value?.role === 'admin'
    })

    const isStaff = computed(() => {
        return ['admin', 'staff'].includes(user.value?.role)
    })

    const isWorkshop = computed(() => {
        return ['admin', 'workshop'].includes(user.value?.role)
    })

    const isCustomer = computed(() => {
        return ['customer', 'admin', 'staff'].includes(user.value?.role)
    })

    // Actions
    const initialize = async () => {
        if (isInitialized.value) return

        try {
            if (token.value) {
                // Verify token and get user info
                const response = await authAPI.getCurrentUser()
                user.value = response.data.user
            }
        } catch (error) {
            console.error('Failed to initialize user store:', error)
            // Only logout if it's a 401/403 error (invalid token)
            if (error.response?.status === 401 || error.response?.status === 403) {
                logout()
            }
        } finally {
            isInitialized.value = true
        }
    }

    const login = async (credentials) => {
        try {
            const response = await authAPI.login(credentials)
            const { token: newToken, user: userData } = response.data

            // Store token and user data
            token.value = newToken
            user.value = userData
            localStorage.setItem('token', newToken)

            // Set auth header for future requests
            api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

            return { success: true }
        } catch (error) {
            console.error('Login failed:', error)
            // Check for specific error types from backend
            const errorData = error.response?.data
            if (errorData?.error === 'AccountDeactivated') {
                return {
                    success: false,
                    error: errorData.message || 'Account has been deactivated'
                }
            } else if (errorData?.error === 'InvalidCredentials') {
                return {
                    success: false,
                    error: errorData.message || 'Invalid email/phone or password'
                }
            }
            return {
                success: false,
                error: errorData?.message || 'Login failed'
            }
        }
    }


    const logout = () => {
        // Clear user data
        user.value = null
        token.value = null
        loginMethod.value = null
        localStorage.removeItem('token')
        localStorage.removeItem('loginMethod')

        // Clear auth header
        delete api.defaults.headers.common['Authorization']
    }

    // User-initiated sign out: end the session on the server too, so the token
    // stops working. logout() stays local-only since it also runs on 401s.
    const signOut = async () => {
        try {
            if (token.value) await authAPI.logout()
        } catch (error) {
            // Session may already be gone; clear locally regardless
        } finally {
            logout()
        }
    }

    const updateUser = (userData) => {
        user.value = { ...user.value, ...userData }
    }

    const accessSecretProduct = async (secretKey) => {
        try {
            console.log('Attempting to access secret product with key:', secretKey)
            // Validate secret key by trying to access the product
            const response = await api.get(`/products/secret/${secretKey}`)

            console.log('Secret product API response:', response.data)

            if (response.data.product) {
                console.log('Secret product found, storing key in session')
                // Store the secret key in session storage for the product page
                sessionStorage.setItem('currentSecretKey', secretKey)
                return { success: true }
            } else {
                console.log('No product found in response')
                return {
                    success: false,
                    error: 'Invalid secret key'
                }
            }
        } catch (error) {
            console.error('Secret product access failed:', error)
            return {
                success: false,
                error: error.response?.data?.message || 'Invalid secret key or product not found'
            }
        }
    }

    // Initialize auth header if token exists
    if (token.value) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
    }

    // Periodic session validation (every 5 minutes)
    const validateSession = async () => {
        if (token.value && user.value) {
            try {
                await authAPI.getCurrentUser()
            } catch (error) {
                if (error.response?.status === 401) {

                    logout()
                }
            }
        }
    }

    // Set up periodic validation
    if (typeof window !== 'undefined') {
        setInterval(validateSession, 5 * 60 * 1000) // Check every 5 minutes
    }

    return {
        // State
        user,
        token,
        isInitialized,
        loginMethod,

        // Getters
        isAuthenticated,
        isAdmin,
        isStaff,
        isWorkshop,
        isCustomer,

        // Actions
        initialize,
        login,
        logout,
        signOut,
        updateUser,
        accessSecretProduct
    }
}) 